import { useEffect, useState } from 'react';
import { settingsStorage, TouchSettings, TouchSensitivity } from '../services/settingsStorage';

export const useTouchOptimization = () => {
  const [touchSettings, setTouchSettings] = useState<TouchSettings>({
    touchSensitivity: 'normal',
    scrollMode: 'smooth',
    animationSpeed: 150,
    gpuAcceleration: true,
  });

  useEffect(() => {
    const loadSettings = async () => {
      const settings = await settingsStorage.getTouchSettings();
      setTouchSettings(settings);
      applyTouchOptimizations(settings);
    };
    loadSettings();
  }, []);

  const applyTouchOptimizations = (settings: TouchSettings) => {
    const root = document.documentElement;

    root.style.setProperty('--animation-duration', `${settings.animationSpeed}ms`);

    if (settings.gpuAcceleration) {
      root.style.setProperty('--gpu-acceleration', 'translateZ(0)');
    } else {
      root.style.setProperty('--gpu-acceleration', 'none');
    }

    if (settings.scrollMode === 'smooth') {
      root.style.setProperty('scroll-behavior', 'smooth');
      root.style.setProperty('-webkit-overflow-scrolling', 'touch');
    } else if (settings.scrollMode === 'snap') {
      root.style.setProperty('scroll-snap-type', 'x mandatory');
    } else {
      root.style.setProperty('scroll-behavior', 'auto');
    }
  };

  const updateTouchSettings = async (newSettings: Partial<TouchSettings>) => {
    const updated = { ...touchSettings, ...newSettings };
    await settingsStorage.saveTouchSettings(updated);
    setTouchSettings(updated);
    applyTouchOptimizations(updated);
  };

  const getTouchClasses = (baseClasses: string = ''): string => {
    const sensitivity = touchSettings.touchSensitivity;
    const sensitivityClasses = getSensitivityClasses(sensitivity);
    return `${baseClasses} ${sensitivityClasses} touch-manipulation`;
  };

  const getSensitivityClasses = (sensitivity: TouchSensitivity): string => {
    switch (sensitivity) {
      case 'high':
        return 'touch-area-high';
      case 'very-high':
        return 'touch-area-very-high';
      default:
        return '';
    }
  };

  return {
    touchSettings,
    updateTouchSettings,
    getTouchClasses,
  };
};
